// member-portal\src\components\AuthForms.js — FULL FILE (unchanged — already working)
// Referral code auto-populates from URL + read-only if present

import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Loader } from '@googlemaps/js-api-loader';
import './AuthForms.css';

class ErrorBoundary extends React.Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="error-container">
          <p className="error">Something went wrong. Please try again or disable browser extensions.</p>
          <button onClick={() => this.setState({ hasError: false, error: null })}>
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const AuthForms = ({ type, auth, setView }) => {
  const { handleLogin, handleSignup, error, passwordError, loading, showSignupPrompt, handleSignupPromptResponse, showDeactivatedPrompt, handleDeactivatedPromptResponse, showSignupConfirmation, handleSignupConfirmationClose } = auth;
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    surname: '',
    email: '',
    position: '',
    companyName: '',
    vatNumber: '',
    contactNumber: '',
    countryCode: '+27',
    whatsappNumber: '',
    emailAccounts: '',
    emailNotifications: '',
    businessAddress: '',
    companyLogo: '',
    companyLogoFile: null,
    enterpriseName: '',
    registrationNumber: '',
    termsAccepted: false,
    referralCode: '', // ← NEW FIELD for referral system
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isCountryCodeOpen, setIsCountryCodeOpen] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const [isAddressFocused, setIsAddressFocused] = useState(false);
  const [isUsernameFocused, setIsUsernameFocused] = useState(false);
  const [showForgotForm, setShowForgotForm] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const addressInputRef = useRef(null);
  const loginButtonRef = useRef(null);
  const countryCodeRef = useRef(null);

  // Auto-populate referral code from URL query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      const upperRef = ref.trim().toUpperCase();
      setFormData(prev => ({ ...prev, referralCode: upperRef }));
    }
  }, []);

  // Reset form data when view changes to login
  useEffect(() => {
    if (type === 'login') {
      setFormData({
        username: '',
        password: '',
        name: '',
        surname: '',
        email: '',
        position: '',
        companyName: '',
        vatNumber: '',
        contactNumber: '',
        countryCode: '+27',
        whatsappNumber: '',
        emailAccounts: '',
        emailNotifications: '',
        businessAddress: '',
        companyLogo: '',
        companyLogoFile: null,
        enterpriseName: '',
        registrationNumber: '',
        termsAccepted: false,
        referralCode: '',
      });
      setShowForgotForm(false);
      setForgotEmail('');
      setForgotMessage('');
      setForgotError('');
    }
  }, [type]);

  useEffect(() => {
    const loader = new Loader({
      apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
      version: 'weekly',
      libraries: ['places'],
    });

    loader
      .load()
      .then(() => {
        setGoogleLoaded(true);
      })
      .catch((err) => {
        console.error('Failed to load Google Maps JavaScript API:', err);
      });
  }, []);

  useEffect(() => {
    if (googleLoaded && addressInputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['establishment', 'geocode'],
        componentRestrictions: { country: 'ZA' },
      });

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();
          const formattedAddress = place.formatted_address || place.name || '';
          setFormData((prev) => ({
            ...prev,
            businessAddress: formattedAddress,
            location: { lat, lng },
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            location: { lat: -34.0837, lng: 18.4448 },
          }));
        }
      });
    }
  }, [googleLoaded]);

  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/csrf-token`, {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok) {
          setCsrfToken(data.csrfToken);
        } else {
          setForgotError('Failed to initialize password reset. Please try again.');
        }
      } catch (err) {
        console.error('Error fetching CSRF token:', err);
        setForgotError('Failed to initialize password reset. Please try again.');
      }
    };
    fetchCsrfToken();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updatedData = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'contactNumber' || name === 'whatsappNumber') {
        updatedData[name] = value.replace(/\D/g, '');
      }
      // Prevent changing referralCode if it came from URL
      if (name === 'referralCode' && new URLSearchParams(window.location.search).get('ref')) {
        return prev;
      }
      return updatedData;
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        auth.setError('Only JPEG and PNG images are allowed');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        auth.setError('File size must be less than 5MB');
        return;
      }
      setFormData((prev) => ({
        ...prev,
        companyLogoFile: file,
        companyLogo: '',
      }));
      auth.setError('');
    }
  };

  const uploadFileToS3 = async (file, token) => {
    try {
      console.log('Uploading logo to S3:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        token: token ? 'Present' : 'Missing'
      });
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/deals/presigned-url`,
        {
          fileType: file.type,
          fileName: file.name,
          type: 'logo',
        },
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      console.log('Presigned URL response:', response.data);
      const { url, cloudfrontUrl } = response.data;

      console.log('Sending PUT request to S3:', { url });
      await axios.put(url, file, {
        headers: { 'Content-Type': file.type },
      });
      console.log('S3 upload successful, CloudFront URL:', cloudfrontUrl);

      return cloudfrontUrl;
    } catch (error) {
      console.error('Error uploading logo:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        } : 'No response',
        request: error.request ? error.request : 'No request'
      });
      throw new Error('Failed to upload logo');
    }
  };

  const handleCountryCodeSelect = (value) => {
    setFormData((prev) => ({ ...prev, countryCode: value }));
    setIsCountryCodeOpen(false);
  };

  const toggleCountryCodeDropdown = () => {
    setIsCountryCodeOpen((prev) => !prev);
  };

  const handleClickOutside = (event) => {
    if (countryCodeRef.current && !countryCodeRef.current.contains(event.target)) {
      setIsCountryCodeOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({ ...prev, password }));
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleNext = () => setStep(2);

  const handleBackToStep1 = () => setStep(1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (type === 'login') {
      await handleLogin(formData.username, formData.password);
    } else if (type === 'signup' && step === 2) {
      console.log('Submitting signup data:', formData);
      try {
        let companyLogo = formData.companyLogo;
        if (formData.companyLogoFile) {
          const token = auth.token || localStorage.getItem('token');
          if (!token) {
            console.warn('No token available for S3 upload during signup');
          }
          companyLogo = await uploadFileToS3(formData.companyLogoFile, token);
          setFormData((prev) => ({ ...prev, companyLogo }));
        }
        const signupData = {
          ...formData,
          companyLogo,
        };
        await handleSignup(signupData);
      } catch (err) {
        console.error('Submit error:', err);
        auth.setError(err.message);
      }
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');

    if (!forgotEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail)) {
      setForgotError('Please enter a valid email address.');
      return;
    }

    if (!csrfToken) {
      setForgotError('CSRF token not available. Please try again.');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ email: forgotEmail.toLowerCase() }),
      });

      const data = await response.json();
      if (response.ok) {
        setForgotMessage(data.message);
        setCsrfToken(data.csrfToken || csrfToken);
        setForgotEmail('');
        setTimeout(() => setShowForgotForm(false), 3000);
      } else if (response.status === 429) {
        setForgotError('Too many requests. Please wait a few minutes and try again.');
      } else {
        setForgotError(data.message || 'Failed to send reset link. Please try again.');
        setCsrfToken(data.csrfToken || csrfToken);
      }
    } catch (err) {
      console.error('Forgot password error:', err);
      setForgotError('Server error. Please try again later.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && formData.username && formData.password) {
      e.preventDefault();
      loginButtonRef.current.click();
    }
  };

  const formatContactNumber = (value) => {
    const nums = value.replace(/\D/g, '');
    if (nums.length <= 3) return nums;
    if (nums.length <= 6) return `${nums.slice(0, 3)}-${nums.slice(3)}`;
    return `${nums.slice(0, 3)}-${nums.slice(3, 6)}-${nums.slice(6, 10)}`;
  };

  return (
    <ErrorBoundary>
      <div className="auth-container">
        <div className={`auth-forms ${step === 2 ? 'member-details' : ''}`}>
          <div className="auth-forms-inner">
            <h2>{type === 'signup' && step === 1 ? 'Organization Details' : type === 'signup' ? 'Member Details' : showForgotForm ? 'Reset Password' : 'Login'}</h2>
            {error && <p className="error">{error}</p>}
            <form id="auth-form" onSubmit={showForgotForm ? handleForgotPassword : handleSubmit}>
              {type === 'signup' && step === 1 && (
                <>
                  <div className="form-group">
                    <label>Company Name *</label>
                    <div className="input-wrapper">
                      <input name="companyName" value={formData.companyName} onChange={handleChange} required />
                      <div className="tooltip">
                        This is the name of your restaurant that customers will see next to your deals in the Scover app.
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Enterprise Name *</label>
                    <div className="input-wrapper">
                      <input name="enterpriseName" value={formData.enterpriseName} onChange={handleChange} required />
                      <div className="tooltip">
                        This is your business's official name registered with the CIPC. We use it to verify your business, but it won’t be shown publicly.
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>CIPC Registration Number *</label>
                    <input name="registrationNumber" value={formData.registrationNumber} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>VAT Number (optional)</label>
                    <input name="vatNumber" value={formData.vatNumber} onChange={handleChange} />
                  </div>

                  {/* ─────────────────────────────────────────────────────────────
                       FIXED: Referral code auto-filled from URL + read-only
                       Prevents manual edit when coming from referral link
                       ───────────────────────────────────────────────────────────── */}
                  <div className="form-group">
                    <label>Referral Code (optional)</label>
                    <input
                      name="referralCode"
                      value={formData.referralCode}
                      onChange={handleChange}
                      readOnly={!!new URLSearchParams(window.location.search).get('ref')}
                      placeholder="Enter referral code if you have one"
                      className={new URLSearchParams(window.location.search).get('ref') ? 'readonly-input' : ''}
                    />
                    {new URLSearchParams(window.location.search).get('ref') && (
                      <small className="referral-hint">Applied automatically from referral link</small>
                    )}
                  </div>
                  {/* ───────────────────────────────────────────────────────────── */}

                  <div className="form-group">
                    <label className="tel-label">Tel *</label>
                    <div className="contact-input">
                      <div className="custom-select" ref={countryCodeRef}>
                        <div className="selected-option" onClick={toggleCountryCodeDropdown}>
                          {formData.countryCode}
                        </div>
                        {isCountryCodeOpen && (
                          <div className="options">
                            <div className="option" onClick={() => handleCountryCodeSelect('+27')}>
                              +27
                            </div>
                          </div>
                        )}
                      </div>
                      <input
                        name="contactNumber"
                        value={formatContactNumber(formData.contactNumber)}
                        onChange={handleChange}
                        placeholder="082-123-4567"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="tel-label">WhatsApp (optional)</label>
                    <div className="contact-input">
                      <div className="custom-select" ref={countryCodeRef}>
                        <div className="selected-option" onClick={toggleCountryCodeDropdown}>
                          {formData.countryCode}
                        </div>
                        {isCountryCodeOpen && (
                          <div className="options">
                            <div className="option" onClick={() => handleCountryCodeSelect('+27')}>
                              +27
                            </div>
                          </div>
                        )}
                      </div>
                      <input
                        name="whatsappNumber"
                        value={formatContactNumber(formData.whatsappNumber)}
                        onChange={handleChange}
                        placeholder="082-123-4567"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Accounts Email *</label>
                    <input type="email" name="emailAccounts" value={formData.emailAccounts} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Notifications Email *</label>
                    <input type="email" name="emailNotifications" value={formData.emailNotifications} onChange={handleChange} required />
                  </div>
                  <div className="form-group relative">
                    <label>Business Address *</label>
                    <div className="flex items-center">
                      <input
                        ref={addressInputRef}
                        name="businessAddress"
                        value={formData.businessAddress}
                        onChange={handleChange}
                        onFocus={() => setIsAddressFocused(true)}
                        onBlur={() => setIsAddressFocused(false)}
                        required
                        placeholder="Enter your address"
                        className="w-full"
                      />
                      {isAddressFocused && (
                        <div className="speech-bubble">
                          Please select your business's Google Places listing if available, or choose your address from the Google Maps suggestions. This helps us accurately locate your business.
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Company Logo *</label>
                    {formData.companyLogo && (
                      <div className="logo-preview">
                        <img
                          src={formData.companyLogo}
                          alt="Company Logo"
                          className="company-logo-preview"
                          onError={(e) => (e.target.style.display = 'none')}
                        />
                      </div>
                    )}
                    <div className="input-wrapper">
                      <input
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={handleFileChange}
                        className="form-input"
                      />
                      <div className="tooltip">
                        Recommended size: 96x96px (square), ~10-20KB
                      </div>
                    </div>
                  </div>
                  <div className="form-group terms-group">
                    <label className="terms-label">
                      <input
                        type="checkbox"
                        name="termsAccepted"
                        checked={formData.termsAccepted}
                        onChange={handleChange}
                        required
                      />
                      I accept the <a href="https://www.scover.co.za/legal" target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                    </label>
                  </div>
                  <div className="auth-button-group">
                    <button type="button" onClick={handleNext} disabled={loading || !formData.termsAccepted} className="next-button">{loading ? 'Processing...' : 'Next'}</button>
                    <button type="button" onClick={() => setView('landing')} className="back-button">Back</button>
                  </div>
                </>
              )}
              {type === 'signup' && step === 2 && (
                <>
                  <div className="form-group">
                    <label>Name *</label>
                    <input name="name" value={formData.name} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Surname *</label>
                    <input name="surname" value={formData.surname} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input name="email" value={formData.email} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Position *</label>
                    <input name="position" value={formData.position} onChange={handleChange} required />
                  </div>
                  <div className="form-group">
                    <label>Username *</label>
                    <input name="username" value={formData.username} onChange={handleChange} required />
                  </div>
                  <div className="form-group password-group">
                    <label>Password *</label>
                    <div className="password-input">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                      />
                      <button type="button" onClick={togglePasswordVisibility} className="view-password">
                        {showPassword ? (
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 7l-10 10M7 7l10 10M12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7z"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                    <button type="button" className="generate-password" onClick={generatePassword}>
                      Generate Strong Password
                    </button>
                  </div>
                  <div className="auth-button-group">
                    <button type="submit" disabled={loading} className="next-button">{loading ? 'Signing Up...' : 'Sign Up'}</button>
                    <button type="button" onClick={handleBackToStep1} className="back-button">Back</button>
                  </div>
                </>
              )}
              {type === 'login' && !showForgotForm && (
                <>
                  <div className="form-group relative">
                    <label>Username</label>
                    <div className="flex items-center">
                      <input
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        onKeyPress={handleKeyPress}
                        onFocus={() => setIsUsernameFocused(true)}
                        onBlur={() => setIsUsernameFocused(false)}
                        required
                        className="w-full"
                      />
                      {isUsernameFocused && (
                        <div className="speech-bubble">
                          Please sign in with the email or username you chose at signup. Note: usernames are case-sensitive.
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-group password-group">
                    <label>Password</label>
                    <div className="password-input">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        onKeyPress={handleKeyPress}
                        required
                      />
                      <button type="button" onClick={togglePasswordVisibility} className="view-password">
                        {showPassword ? (
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                          </svg>
                        ) : (
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 7l-10 10M7 7l10 10M12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7 1.274-4.057 5.064-7 9.542-7z"></path>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="auth-button-group">
                    <button type="submit" form="auth-form" disabled={loading} className="login-button" ref={loginButtonRef}>
                      {loading ? 'Logging In...' : 'Login'}
                    </button>
                    <button type="button" onClick={() => setView('landing')} className="back-button">Back</button>
                  </div>
                  <div className="forgot-password-container">
                    <span className="forgot-password-text">Forgot your password?</span>
                    <button onClick={() => setShowForgotForm(true)} className="forgot-password-button" disabled={loading}>
                      Reset Password
                    </button>
                  </div>
                </>
              )}
              {type === 'login' && showForgotForm && (
                <>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="Enter your email"
                      required
                      className="form-input"
                      disabled={loading}
                    />
                  </div>
                  <div className="auth-button-group">
                    <button type="submit" disabled={loading} className="login-button">
                      {loading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                    <button type="button" onClick={() => setShowForgotForm(false)} className="back-button" disabled={loading}>
                      Back to Login
                    </button>
                  </div>
                  {forgotError && <p className="error">{forgotError}</p>}
                  {forgotMessage && <p className="success">{forgotMessage}</p>}
                </>
              )}
              {passwordError && <p className="password-error">{passwordError}</p>}
              {showSignupPrompt && (
                <div className="signup-prompt">
                  <div className="prompt-content">
                    <p>No organization associated with the credentials provided. Do you want to sign up your company?</p>
                    <div className="prompt-buttons">
                      <button onClick={() => handleSignupPromptResponse('cancel')} className="prompt-button cancel">
                        Cancel
                      </button>
                      <button onClick={() => handleSignupPromptResponse('signup')} className="prompt-button signup">
                        Sign Up
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showDeactivatedPrompt && (
                <div className="signup-prompt">
                  <div className="prompt-content">
                    <p>This account has been deactivated. Please contact the administrator of the organization.</p>
                    <div className="prompt-buttons">
                      <button onClick={handleDeactivatedPromptResponse} className="prompt-button cancel">
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showSignupConfirmation && (
                <div className="signup-prompt">
                  <div className="prompt-content">
                    <p>Thank you for signing up with Scover! We've sent a verification email to your provided email address. Please check your inbox (and spam folder) to verify your account.</p>
                    <div className="prompt-buttons">
                      <button onClick={handleSignupConfirmationClose} className="prompt-button signup">
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default AuthForms;